import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { FaCopy, FaMagic, FaPlus, FaSearch } from 'react-icons/fa';
import { Link, useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../components/ui/LoadingSpinner';
import PromptCard from '../components/ui/PromptCard';
import { PromptGenerationService } from '../services/llmClient';
import { selectAuthUserId } from '../redux/authSlice';
import { selectCourses } from '../redux/coursesSlice';
import { fetchUserProjects } from '../services/supabaseClient/projectService';
import eventStreamService from '../services/supabaseClient/eventStreamService';
import {
  getGeneratedPrompt,
  listGeneratedPrompts,
  saveGeneratedPrompt,
} from '../services/supabaseClient/generatedPromptsService';
import {
  buildPromptTags,
  formatScalarValue,
  getPromptLinkIds,
  getPromptTitle,
  normalizeList,
  parseConstraints,
} from './promptStudioUtils';
import './PromptsPage.css';

const PROMPT_TYPE_OPTIONS = [
  ['project', 'Project'],
  ['assignment', 'Assignment'],
  ['planning', 'Planning'],
  ['research', 'Research'],
  ['design', 'Design'],
  ['testing', 'Testing'],
  ['critique', 'Critique'],
  ['study', 'Study'],
  ['teaching', 'Teaching'],
  ['automation', 'Automation'],
];

const GENERATION_MODE_OPTIONS = [
  ['implementation', 'Implementation'],
  ['planning', 'Planning'],
  ['assignment', 'Assignment'],
  ['design', 'Design'],
  ['testing', 'Testing'],
  ['critique', 'Critique'],
  ['research', 'Research'],
  ['debugging', 'Debugging'],
  ['review', 'Review'],
  ['teaching', 'Teaching'],
];

const CUSTOM_OPTION_VALUE = '__custom__';

const DESIRED_OUTPUT_SUGGESTIONS = [
  'implementation prompt',
  'planning prompt',
  'design prompt',
  'testing prompt',
  'critique prompt',
  'research prompt',
  'debugging prompt',
  'code review prompt',
  'architecture prompt',
  'product spec prompt',
  'lesson prompt',
  'study guide prompt',
  'automation prompt',
  'deployment prompt',
];

const DIFFICULTY_SCOPE_SUGGESTIONS = [
  'mvp',
  'prototype',
  'production-ready',
  'beginner-friendly',
  'intermediate',
  'advanced',
  'small scoped',
  'medium scoped',
  'deep dive',
  'rapid iteration',
];

const GOAL_SUGGESTIONS_BY_MODE = {
  implementation: [
    'Turn the linked planning and design decisions into an implementation-ready prompt.',
    'Generate a prompt that helps an engineer build the first working version of this system.',
  ],
  planning: [
    'Generate a prompt that scopes the problem, breaks down milestones, and surfaces tradeoffs.',
    'Create a planning prompt that defines phases, risks, and success criteria.',
  ],
  design: [
    'Generate a prompt that explores UX, architecture, and information design decisions.',
    'Create a design prompt that balances usability, brand, and implementation feasibility.',
  ],
  testing: [
    'Generate a prompt that produces a testing strategy, edge cases, and validation criteria.',
    'Create a QA prompt covering unit, integration, and user-flow testing.',
  ],
  critique: [
    'Generate a prompt that critiques the current approach and highlights risks or weak spots.',
    'Create a review prompt that evaluates decisions against constraints and outcomes.',
  ],
  research: [
    'Generate a prompt that compares approaches, references prior learning, and recommends a path.',
    'Create a research prompt that investigates alternatives before implementation.',
  ],
  debugging: [
    'Generate a prompt that diagnoses likely causes, debugging steps, and expected fixes.',
    'Create a debugging prompt that isolates regressions and proposes targeted checks.',
  ],
  review: [
    'Generate a prompt for reviewing code or product decisions against quality criteria.',
    'Create a structured review prompt focused on bugs, regressions, and missing tests.',
  ],
  teaching: [
    'Generate a prompt that teaches this topic through examples, exercises, and explanations.',
    'Create a teaching prompt that adapts course concepts to a practical walkthrough.',
  ],
  assignment: [
    'Generate a prompt that helps complete the assignment while applying course knowledge.',
    'Create an assignment prompt that is structured, constraint-aware, and submission-ready.',
  ],
};

const DEFAULT_FORM = {
  type: 'project',
  generationMode: 'implementation',
  courseId: '',
  projectId: '',
  linkedPromptIds: [],
  title: '',
  goal: '',
  targetOutcome: '',
  constraints: '',
  preferredStack: '',
  desiredOutput: 'implementation prompt',
  difficultyScope: 'mvp',
};

const PROMPT_DRAFT_STORAGE_PREFIX = 'ml_prompt_studio_draft';

const buildSourcePromptArray = ({ selectedIds, promptIndex }) => {
  const visited = new Set();
  const ordered = [];

  const visit = (promptId) => {
    const normalizedId = String(promptId);
    if (!normalizedId || visited.has(normalizedId)) return;

    const prompt = promptIndex.get(normalizedId);
    if (!prompt) return;

    visited.add(normalizedId);
    getPromptLinkIds(prompt).forEach((linkedId) => visit(linkedId));
    ordered.push(prompt);
  };

  normalizeList(selectedIds).forEach((promptId) => visit(promptId));

  return ordered.map((prompt, index) => ({
    id: String(prompt.id),
    title: getPromptTitle(prompt),
    type: prompt.type || 'project',
    generationMode: prompt.metadata?.mode || prompt.generation_mode || 'implementation',
    prompt: prompt.generated_prompt || '',
    role: index === 0 ? 'root_input' : 'linked_input',
    position: index,
    tags: buildPromptTags(prompt),
  }));
};

const buildCourseSnapshot = (course) => {
  if (!course) return null;
  return {
    id: course.id,
    title: course.title,
    description: course.description,
    difficultyLevel: course.difficultyLevel || course.difficulty_level || null,
    currentLessonIndex: course.currentLessonIndex ?? 0,
    objectives: normalizeList(course.objectives),
    priorKnowledge: normalizeList(course.priorKnowledge),
    units: normalizeList(course.units).map((unit, index) => ({
      id: unit.id || `unit_${index + 1}`,
      title: unit.title || unit.unit_title || `Unit ${index + 1}`,
      position: unit.position ?? unit.unit_index ?? index,
      objective: unit.objective || unit.summary || '',
      assessment: unit.assessment || '',
      constraints: normalizeList(unit.constraints),
      failureModes: normalizeList(unit.failureModes || unit.failure_modes),
    })),
    notes: normalizeList(course.notes || course.lessonNotes).map((note, index) => ({
      id: note.id || `note_${index + 1}`,
      title: note.title || note.heading || `Note ${index + 1}`,
      summary: note.summary || note.text || '',
      constraints: normalizeList(note.constraints),
      failureModes: normalizeList(note.failureModes || note.failure_modes),
    })),
  };
};

const PromptsPage = () => {
  const [searchParams] = useSearchParams();
  const userId = useSelector(selectAuthUserId);
  const courses = useSelector(selectCourses);
  const draftStorageKey = userId
    ? `${PROMPT_DRAFT_STORAGE_PREFIX}:${userId}`
    : PROMPT_DRAFT_STORAGE_PREFIX;

  const [projects, setProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [savedPrompts, setSavedPrompts] = useState([]);
  const [savedPromptsLoading, setSavedPromptsLoading] = useState(false);
  const [generationLoading, setGenerationLoading] = useState(false);
  const [error, setError] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [generatedPromptMeta, setGeneratedPromptMeta] = useState(null);
  const [selectedPromptId, setSelectedPromptId] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTypeFilter, setActiveTypeFilter] = useState('all');
  const [sortMode, setSortMode] = useState('recent');
  const [linkedPromptPicker, setLinkedPromptPicker] = useState('');
  const [dashboardLinkPicker, setDashboardLinkPicker] = useState('');
  const [linkingPromptId, setLinkingPromptId] = useState('');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const rawDraft = window.localStorage.getItem(draftStorageKey);
      if (!rawDraft) return;

      const parsedDraft = JSON.parse(rawDraft);
      if (!parsedDraft || typeof parsedDraft !== 'object') return;

      setForm((prev) => ({
        ...prev,
        ...Object.fromEntries(
          Object.entries(parsedDraft).filter(
            ([key, value]) =>
              typeof value === 'string' || (key === 'linkedPromptIds' && Array.isArray(value))
          )
        ),
      }));
    } catch {
      // Ignore malformed drafts.
    }
  }, [draftStorageKey]);

  useEffect(() => {
    const seededType = searchParams.get('type') || DEFAULT_FORM.type;
    const seededMode = searchParams.get('mode') || DEFAULT_FORM.generationMode;
    const seededCourseId = searchParams.get('courseId') || '';
    const seededProjectId = searchParams.get('projectId') || '';

    setForm((prev) => ({
      ...prev,
      type: seededType,
      generationMode: seededMode,
      courseId: prev.courseId || seededCourseId,
      projectId: prev.projectId || seededProjectId,
    }));

    if (seededCourseId || seededProjectId) {
      setShowCreateModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      window.localStorage.setItem(draftStorageKey, JSON.stringify(form));
    } catch {
      // Ignore storage failures.
    }
  }, [draftStorageKey, form]);

  useEffect(() => {
    const loadProjects = async () => {
      if (!userId) return;
      setProjectsLoading(true);
      const { data, error: projectsError } = await fetchUserProjects();
      if (projectsError) {
        setError(projectsError.message || 'Failed to load projects');
      } else {
        setProjects(data || []);
      }
      setProjectsLoading(false);
    };

    loadProjects();
  }, [userId]);

  useEffect(() => {
    const loadSavedPrompts = async () => {
      if (!userId) return;
      setSavedPromptsLoading(true);
      const { data, error: promptsError } = await listGeneratedPrompts({ userId });
      if (promptsError) {
        setError(promptsError.message || 'Failed to load prompts');
      } else {
        setSavedPrompts(data || []);
      }
      setSavedPromptsLoading(false);
    };

    loadSavedPrompts();
  }, [userId]);

  const selectedCourse = useMemo(
    () => courses.find((course) => String(course.id) === String(form.courseId)) || null,
    [courses, form.courseId]
  );

  const selectedProject = useMemo(
    () => projects.find((project) => String(project.id) === String(form.projectId)) || null,
    [projects, form.projectId]
  );

  const selectedLinkedPrompts = useMemo(
    () =>
      normalizeList(form.linkedPromptIds)
        .map(
          (linkedId) =>
            savedPrompts.find((prompt) => String(prompt.id) === String(linkedId)) || null
        )
        .filter(Boolean),
    [savedPrompts, form.linkedPromptIds]
  );

  useEffect(() => {
    if (!selectedProject) return;
    setForm((prev) => ({
      ...prev,
      courseId: prev.courseId || selectedProject.course_id || '',
      title: prev.title || selectedProject.title || '',
      goal: prev.goal || selectedProject.description || '',
      targetOutcome:
        prev.targetOutcome ||
        normalizeList(selectedProject.deliverables).join(', ') ||
        'A working deliverable',
      preferredStack: prev.preferredStack || normalizeList(selectedProject.technologies).join(', '),
      constraints:
        prev.constraints ||
        normalizeList(selectedProject.technologies)
          .concat(
            selectedProject.estimated_hours
              ? [`Keep scoped to about ${selectedProject.estimated_hours} hours`]
              : []
          )
          .join('\n'),
      desiredOutput: prev.desiredOutput || selectedProject.project_type || 'implementation prompt',
      difficultyScope: prev.difficultyScope || selectedProject.difficulty_level || 'mvp',
    }));
  }, [selectedProject]);

  const selectedPrompt = useMemo(
    () => savedPrompts.find((item) => item.id === selectedPromptId) || null,
    [savedPrompts, selectedPromptId]
  );

  const selectedPromptWorkflow = useMemo(() => {
    if (!selectedPrompt) {
      return { upstream: [], downstream: [] };
    }

    const upstreamIds = getPromptLinkIds(selectedPrompt);
    const upstream = upstreamIds
      .map((id) => savedPrompts.find((prompt) => String(prompt.id) === String(id)) || null)
      .filter(Boolean);

    const downstream = savedPrompts.filter((prompt) =>
      getPromptLinkIds(prompt).includes(String(selectedPrompt.id))
    );

    return { upstream, downstream };
  }, [savedPrompts, selectedPrompt]);

  const availableLinkedPrompts = useMemo(
    () =>
      savedPrompts.filter(
        (prompt) =>
          String(prompt.id) !== String(selectedPromptId) &&
          !normalizeList(form.linkedPromptIds).includes(String(prompt.id))
      ),
    [savedPrompts, selectedPromptId, form.linkedPromptIds]
  );

  const availableDashboardLinks = useMemo(() => {
    if (!selectedPrompt) return [];

    const selectedLinkIds = getPromptLinkIds(selectedPrompt);
    return savedPrompts.filter(
      (prompt) =>
        String(prompt.id) !== String(selectedPrompt.id) &&
        !selectedLinkIds.includes(String(prompt.id))
    );
  }, [savedPrompts, selectedPrompt]);

  const activeGoalSuggestions = useMemo(
    () => GOAL_SUGGESTIONS_BY_MODE[form.generationMode] || GOAL_SUGGESTIONS_BY_MODE.implementation,
    [form.generationMode]
  );

  const isCustomPromptType = useMemo(
    () => !PROMPT_TYPE_OPTIONS.some(([value]) => value === form.type),
    [form.type]
  );

  const isCustomGenerationMode = useMemo(
    () => !GENERATION_MODE_OPTIONS.some(([value]) => value === form.generationMode),
    [form.generationMode]
  );

  const promptIndex = useMemo(
    () => new Map(savedPrompts.map((prompt) => [String(prompt.id), prompt])),
    [savedPrompts]
  );

  const filteredPrompts = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const next = savedPrompts.filter((item) => {
      if (activeTypeFilter !== 'all' && item.type !== activeTypeFilter) {
        return false;
      }

      if (!query) return true;

      const haystack = [
        item.input_summary?.title,
        item.input_summary?.goal,
        item.context?.projectTitle,
        item.context?.courseTitle,
        item.generation_mode,
        item.type,
        item.generated_prompt,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(query);
    });

    return next.sort((a, b) => {
      if (sortMode === 'oldest') {
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }

      if (sortMode === 'title') {
        const aTitle = a.input_summary?.title || a.context?.projectTitle || '';
        const bTitle = b.input_summary?.title || b.context?.projectTitle || '';
        return aTitle.localeCompare(bTitle);
      }

      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [activeTypeFilter, savedPrompts, searchQuery, sortMode]);

  const activePromptOutput = generatedPrompt || selectedPrompt?.generated_prompt || '';
  const activePromptMeta = generatedPrompt ? generatedPromptMeta : selectedPrompt?.metadata || null;

  const handleFieldChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handlePromptTypeSelectChange = (value) => {
    if (value === CUSTOM_OPTION_VALUE) {
      setForm((prev) => ({
        ...prev,
        type: isCustomPromptType ? prev.type : '',
      }));
      return;
    }

    handleFieldChange('type', value);
  };

  const handleGenerationModeSelectChange = (value) => {
    if (value === CUSTOM_OPTION_VALUE) {
      setForm((prev) => ({
        ...prev,
        generationMode: isCustomGenerationMode ? prev.generationMode : '',
      }));
      return;
    }

    handleFieldChange('generationMode', value);
  };

  const handleAddLinkedPrompt = () => {
    if (!linkedPromptPicker) return;

    setForm((prev) => ({
      ...prev,
      linkedPromptIds: [...new Set([...normalizeList(prev.linkedPromptIds), linkedPromptPicker])],
    }));
    setLinkedPromptPicker('');
  };

  const handleRemoveLinkedPrompt = (promptId) => {
    setForm((prev) => ({
      ...prev,
      linkedPromptIds: normalizeList(prev.linkedPromptIds).filter(
        (linkedId) => String(linkedId) !== String(promptId)
      ),
    }));
  };

  const handleResetDraft = () => {
    const seededType = searchParams.get('type') || DEFAULT_FORM.type;
    const seededMode = searchParams.get('mode') || DEFAULT_FORM.generationMode;
    const seededCourseId = searchParams.get('courseId') || '';
    const seededProjectId = searchParams.get('projectId') || '';

    setForm({
      ...DEFAULT_FORM,
      type: seededType,
      generationMode: seededMode,
      courseId: seededCourseId,
      projectId: seededProjectId,
    });
    setError('');
    setCopyFeedback('');
    setLinkedPromptPicker('');
  };

  const handleOpenCreateModal = () => {
    setError('');
    setCopyFeedback('');
    setShowCreateModal(true);
  };

  const refreshSavedPrompts = async () => {
    if (!userId) return [];

    const { data, error: promptsError } = await listGeneratedPrompts({ userId });
    if (promptsError) {
      throw new Error(promptsError.message || 'Failed to load prompts');
    }

    setSavedPrompts(data || []);
    return data || [];
  };

  const persistPromptLinks = async (prompt, nextLinkedPromptIds) => {
    if (!userId || !prompt) return null;

    setLinkingPromptId(prompt.id);

    try {
      const normalizedIds = [...new Set(normalizeList(nextLinkedPromptIds).map(String))];
      const linkedPrompts = normalizedIds
        .map((id) => savedPrompts.find((candidate) => String(candidate.id) === String(id)) || null)
        .filter(Boolean);

      const { data, error: saveError } = await saveGeneratedPrompt({
        id: prompt.id,
        userId,
        type: prompt.type,
        generationMode: prompt.generation_mode,
        context: {
          ...(prompt.context || {}),
          linkedPromptId: normalizedIds[0] || null,
          linkedPromptIds: normalizedIds,
          linkedPromptTitle: linkedPrompts[0] ? getPromptTitle(linkedPrompts[0]) : null,
          linkedPromptTitles: linkedPrompts.map((linkedPrompt) => getPromptTitle(linkedPrompt)),
        },
        inputSummary: prompt.input_summary || {},
        promptAssetKey: prompt.prompt_asset_key || prompt.metadata?.assetKey || null,
        generatedPrompt: prompt.generated_prompt || '',
        llmModel: prompt.llm_model || prompt.metadata?.llmModel || null,
        metadata: prompt.metadata || {},
      });

      if (saveError) {
        throw new Error(saveError.message || 'Failed to update prompt links');
      }

      const refreshed = await refreshSavedPrompts();
      const refreshedPrompt =
        refreshed.find((candidate) => String(candidate.id) === String(prompt.id)) || data || null;

      if (refreshedPrompt) {
        setSelectedPromptId(refreshedPrompt.id);
      }

      setDashboardLinkPicker('');
      setShowLinkModal(true);
      return refreshedPrompt;
    } finally {
      setLinkingPromptId('');
    }
  };

  const handleGenerate = async () => {
    if (!userId) return;
    const effectiveCourseId = form.courseId || selectedProject?.course_id || '';

    if (!effectiveCourseId) {
      setError('Select a course from the Course dropdown before generating a prompt.');
      return;
    }

    setError('');
    setCopyFeedback('');
    setGenerationLoading(true);

    if (effectiveCourseId !== form.courseId) {
      setForm((prev) => ({ ...prev, courseId: effectiveCourseId }));
    }

    try {
      const { data: events, error: eventsError } = await eventStreamService.fetchEvents({
        userId,
        courseId: effectiveCourseId,
        limit: 80,
      });

      if (eventsError) {
        throw new Error(eventsError.message || 'Failed to load learning history');
      }

      const resolvedCourse =
        selectedCourse || courses.find((course) => String(course.id) === String(effectiveCourseId));

      const successfulEvents = normalizeList(events).filter((event) => event?.success === true);
      const sourcePrompts = buildSourcePromptArray({
        selectedIds: form.linkedPromptIds,
        promptIndex,
      });

      const response = await PromptGenerationService.generatePrompt({
        userId,
        type: form.type,
        generationMode: form.generationMode,
        context: {
          courseId: effectiveCourseId,
          projectId: form.projectId || null,
          linkedPromptId: normalizeList(form.linkedPromptIds)[0] || null,
          linkedPromptIds: normalizeList(form.linkedPromptIds),
          graphSelectionMode: sourcePrompts.length > 0 ? 'upstream' : 'manual',
          rootPromptId:
            normalizeList(form.linkedPromptIds)[normalizeList(form.linkedPromptIds).length - 1] ||
            null,
        },
        input: {
          title: form.title,
          goal: form.goal,
          targetOutcome: form.targetOutcome,
          constraints: parseConstraints(form.constraints),
          preferredStack: form.preferredStack,
          desiredOutput: form.desiredOutput,
          difficultyScope: form.difficultyScope,
        },
        sourcePrompts,
        snapshots: {
          course: buildCourseSnapshot(resolvedCourse),
          learningEvents: successfulEvents,
          linkedPrompt: selectedLinkedPrompts[0]
            ? {
                id: selectedLinkedPrompts[0].id,
                title: getPromptTitle(selectedLinkedPrompts[0]),
                type: selectedLinkedPrompts[0].type || 'project',
                generationMode:
                  selectedLinkedPrompts[0].metadata?.mode ||
                  selectedLinkedPrompts[0].generation_mode ||
                  'implementation',
                generatedPrompt: selectedLinkedPrompts[0].generated_prompt || '',
              }
            : null,
          linkedPrompts: selectedLinkedPrompts.map((prompt) => ({
            id: prompt.id,
            title: getPromptTitle(prompt),
            type: prompt.type || 'project',
            generationMode: prompt.metadata?.mode || prompt.generation_mode || 'implementation',
            generatedPrompt: prompt.generated_prompt || '',
          })),
        },
      });

      setGeneratedPrompt(response.generatedPrompt || '');
      setGeneratedPromptMeta(response.metadata || null);
      setSelectedPromptId(response.promptId || null);

      const { data: saved, error: saveError } = await saveGeneratedPrompt({
        id: response.promptId || undefined,
        userId,
        type: form.type,
        generationMode: form.generationMode,
        context: {
          courseId: effectiveCourseId,
          projectId: form.projectId || null,
          linkedPromptId: normalizeList(form.linkedPromptIds)[0] || null,
          linkedPromptIds: normalizeList(form.linkedPromptIds),
          courseTitle: resolvedCourse?.title || null,
          projectTitle: selectedProject?.title || form.title || null,
          graphSelectionMode: sourcePrompts.length > 0 ? 'upstream' : 'manual',
          rootPromptId:
            normalizeList(form.linkedPromptIds)[normalizeList(form.linkedPromptIds).length - 1] ||
            null,
          linkedPromptTitle: selectedLinkedPrompts[0]
            ? getPromptTitle(selectedLinkedPrompts[0])
            : null,
          linkedPromptTitles: selectedLinkedPrompts.map((prompt) => getPromptTitle(prompt)),
        },
        inputSummary: {
          title: form.title,
          goal: form.goal,
          targetOutcome: form.targetOutcome,
          desiredOutput: form.desiredOutput,
        },
        promptAssetKey: response.metadata?.assetKey || null,
        generatedPrompt: response.generatedPrompt || '',
        llmModel: response.metadata?.llmModel || null,
        metadata: {
          ...(response.metadata || {}),
          graph: {
            derivedFromPromptIds: sourcePrompts.map((prompt) => prompt.id),
            sourcePromptCount: sourcePrompts.length,
            traversalMode: sourcePrompts.length > 0 ? 'upstream' : 'manual',
            traversalOrder: sourcePrompts.map((prompt) => prompt.id),
          },
        },
      });

      if (saveError) {
        throw new Error(saveError.message || 'Prompt generated, but saving failed');
      }

      if (saved) {
        setSelectedPromptId(saved.id);
      }

      await refreshSavedPrompts();
      setShowCreateModal(false);
    } catch (err) {
      setError(err.message || 'Failed to generate prompt');
    } finally {
      setGenerationLoading(false);
    }
  };

  const handleSelectSavedPrompt = async (promptId) => {
    setSelectedPromptId(promptId);
    setGeneratedPrompt('');
    setGeneratedPromptMeta(null);
    setError('');
    setCopyFeedback('');

    const existing = savedPrompts.find((item) => item.id === promptId);
    if (existing?.generated_prompt) {
      return;
    }

    const { data, error: readError } = await getGeneratedPrompt(promptId);
    if (readError) {
      setError(readError.message || 'Failed to load prompt');
      return;
    }

    setSavedPrompts((prev) =>
      prev.map((item) => (item.id === promptId ? { ...item, ...data } : item))
    );
  };

  const handleCopy = async () => {
    if (!activePromptOutput) return;
    try {
      await navigator.clipboard.writeText(activePromptOutput);
      setCopyFeedback('Copied');
    } catch {
      setCopyFeedback('Copy failed');
    }
  };

  const handleOpenDashboardLinker = (promptId) => {
    setSelectedPromptId(promptId);
    setGeneratedPrompt('');
    setGeneratedPromptMeta(null);
    setDashboardLinkPicker('');
    setError('');
    setCopyFeedback('');
    setShowLinkModal(true);
  };

  const handleAddDashboardLink = async () => {
    if (!selectedPrompt || !dashboardLinkPicker) return;

    try {
      setError('');
      await persistPromptLinks(selectedPrompt, [
        ...getPromptLinkIds(selectedPrompt),
        dashboardLinkPicker,
      ]);
    } catch (err) {
      setError(err.message || 'Failed to update prompt links');
    }
  };

  const handleRemoveDashboardLink = async (promptIdToRemove) => {
    if (!selectedPrompt) return;

    try {
      setError('');
      await persistPromptLinks(
        selectedPrompt,
        getPromptLinkIds(selectedPrompt).filter(
          (linkedId) => String(linkedId) !== String(promptIdToRemove)
        )
      );
    } catch (err) {
      setError(err.message || 'Failed to update prompt links');
    }
  };

  if (projectsLoading) {
    return (
      <Container className="prompts-page py-4">
        <LoadingSpinner />
      </Container>
    );
  }

  return (
    <Container className="prompts-page dashboard py-4">
      <section className="dashboard-analytics prompts-studio">
        <div className="dashboard-analytics__header prompts-studio__header">
          <div className="prompts-studio__header-copy">
            <span className="dashboard-analytics__eyebrow">Prompt Studio</span>
          </div>

          <div className="prompts-studio__header-actions">
            <Badge bg="dark" className="prompts-page__badge">
              Pivotal Workflow
            </Badge>
            <Button
              as={Link}
              to="/prompt-graph"
              variant="outline-secondary"
              className="prompts-studio__graph-link"
            >
              Graph
            </Button>
            <Button
              className="prompts-studio__create-btn"
              onClick={handleOpenCreateModal}
              aria-label="New Prompt"
              title="New Prompt"
            >
              <FaPlus />
            </Button>
          </div>
        </div>

        {error ? (
          <Alert variant="warning" className="mb-4">
            {error}
          </Alert>
        ) : null}

        <div className="prompts-studio__toolbar">
          <div className="prompts-studio__search">
            <FaSearch className="prompts-studio__search-icon" />
            <input
              type="text"
              className="form-control"
              placeholder="Search prompts by title, goal, course, or content..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="dashboard-analytics__filters">
            {[
              ['all', 'All'],
              ['project', 'Projects'],
              ['assignment', 'Assignments'],
              ['planning', 'Planning'],
            ].map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={activeTypeFilter === value ? 'is-active' : ''}
                onClick={() => setActiveTypeFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>

          <Form.Select
            className="prompts-studio__sort"
            value={sortMode}
            onChange={(e) => setSortMode(e.target.value)}
          >
            <option value="recent">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="title">Title A-Z</option>
          </Form.Select>
        </div>

        <Row className="g-4">
          <Col xl={7}>
            <Card className="prompts-page__card prompts-studio__list-card">
              <Card.Body>
                <div className="prompts-page__card-head">
                  <div>
                    <h5 className="mb-1">Prompt library</h5>
                    <p className="text-muted mb-0">
                      Open any saved prompt to inspect the full output and metadata.
                    </p>
                  </div>
                  <Badge bg="secondary">{filteredPrompts.length} visible</Badge>
                </div>

                {savedPromptsLoading ? (
                  <LoadingSpinner />
                ) : filteredPrompts.length > 0 ? (
                  <div className="prompts-studio__grid">
                    {filteredPrompts.map((item) => {
                      const title = getPromptTitle(item);
                      const tags = buildPromptTags(item);
                      const linkedPromptTitles = normalizeList(item.context?.linkedPromptTitles);
                      const linkedPromptCount = getPromptLinkIds(item).length;
                      const downstreamCount = savedPrompts.filter((prompt) =>
                        getPromptLinkIds(prompt).includes(String(item.id))
                      ).length;
                      const cardDetails = [
                        {
                          label: 'Course',
                          value: formatScalarValue(item.context?.courseTitle),
                        },
                        {
                          label: 'Project',
                          value: formatScalarValue(item.context?.projectTitle),
                        },
                        ...(linkedPromptCount > 0
                          ? [
                              {
                                label: 'Workflow',
                                value: `${linkedPromptCount} linked${downstreamCount ? ` / ${downstreamCount} downstream` : ''}`,
                              },
                              {
                                label: 'Linked prompts',
                                value: formatScalarValue(
                                  linkedPromptTitles.length > 0
                                    ? linkedPromptTitles.join(', ')
                                    : item.context?.linkedPromptTitle ||
                                        item.context?.linkedPromptId
                                ),
                              },
                            ]
                          : []),
                      ];
                      const cardMetadata = [
                        {
                          label: 'Version',
                          value: formatScalarValue(
                            item.metadata?.assetVersion ?? item.metadata?.version ?? 'v1'
                          ),
                        },
                        {
                          label: 'Mode',
                          value: formatScalarValue(
                            item.metadata?.mode || item.generation_mode || '--'
                          ),
                        },
                        {
                          label: 'Model',
                          value: formatScalarValue(item.llm_model || item.metadata?.llmModel),
                        },
                        {
                          label: 'Asset',
                          value: formatScalarValue(
                            item.prompt_asset_key || item.metadata?.assetKey
                          ),
                        },
                      ];

                      return (
                        <PromptCard
                          key={item.id}
                          item={item}
                          title={title}
                          tags={tags}
                          metadata={cardMetadata}
                          details={cardDetails}
                          selected={selectedPromptId === item.id}
                          onSelect={handleSelectSavedPrompt}
                          onLink={handleOpenDashboardLinker}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="dashboard-analytics__state">
                    No prompts match the current filters yet.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={5}>
            <Card className="prompts-page__card prompts-page__card--output prompts-studio__viewer">
              <Card.Body>
                <div className="prompts-page__output-head">
                  <div>
                    <h5 className="mb-1">Prompt viewer</h5>
                  </div>
                  <div className="prompts-page__output-actions">
                    {copyFeedback ? (
                      <span className="prompts-page__copy-feedback">{copyFeedback}</span>
                    ) : null}
                    <Button
                      variant="outline-secondary"
                      onClick={handleCopy}
                      disabled={!activePromptOutput}
                    >
                      <FaCopy className="me-2" />
                      Copy
                    </Button>
                  </div>
                </div>

                {activePromptOutput ? (
                  <>
                    <pre className="prompts-page__output">{activePromptOutput}</pre>
                    {selectedPrompt ? (
                      <div className="prompts-studio__workflow-panel">
                        <div className="prompts-studio__workflow-head">
                          <h6 className="mb-0">Prompt workflow</h6>
                          <span className="prompts-studio__workflow-summary">
                            {selectedPromptWorkflow.upstream.length} upstream
                            {' · '}
                            {selectedPromptWorkflow.downstream.length} downstream
                          </span>
                        </div>

                        <div className="prompts-studio__workflow-grid">
                          <div className="prompts-studio__workflow-column">
                            <span className="prompts-studio__workflow-label">Builds on</span>
                            <div className="prompts-studio__workflow-links">
                              {selectedPromptWorkflow.upstream.length > 0 ? (
                                selectedPromptWorkflow.upstream.map((prompt) => (
                                  <button
                                    key={`upstream-${prompt.id}`}
                                    type="button"
                                    className="prompts-studio__workflow-node"
                                    onClick={() => handleSelectSavedPrompt(prompt.id)}
                                  >
                                    <strong>{getPromptTitle(prompt)}</strong>
                                    <span>{prompt.type || 'prompt'}</span>
                                  </button>
                                ))
                              ) : (
                                <span className="prompts-studio__workflow-empty">
                                  No upstream prompts linked yet.
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="prompts-studio__workflow-column">
                            <span className="prompts-studio__workflow-label">Feeds into</span>
                            <div className="prompts-studio__workflow-links">
                              {selectedPromptWorkflow.downstream.length > 0 ? (
                                selectedPromptWorkflow.downstream.map((prompt) => (
                                  <button
                                    key={`downstream-${prompt.id}`}
                                    type="button"
                                    className="prompts-studio__workflow-node"
                                    onClick={() => handleSelectSavedPrompt(prompt.id)}
                                  >
                                    <strong>{getPromptTitle(prompt)}</strong>
                                    <span>{prompt.type || 'prompt'}</span>
                                  </button>
                                ))
                              ) : (
                                <span className="prompts-studio__workflow-empty">
                                  No downstream prompts connected yet.
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : null}
                    <div className="prompts-page__meta">
                      <span>
                        Mode: {activePromptMeta?.mode || selectedPrompt?.generation_mode || '--'}
                      </span>
                      <span>Concepts: {activePromptMeta?.conceptCount ?? '--'}</span>
                      <span>Events: {activePromptMeta?.eventCount ?? '--'}</span>
                      <span>
                        Course:{' '}
                        {selectedPrompt?.context?.courseTitle ||
                          courses.find((course) => String(course.id) === String(form.courseId))
                            ?.title ||
                          '--'}
                      </span>
                    </div>
                  </>
                ) : (
                  <div className="dashboard-analytics__state">
                    Select a saved prompt or create a new one to inspect the full output here.
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </section>

      <Modal
        show={showLinkModal}
        onHide={() => setShowLinkModal(false)}
        centered
        dialogClassName="prompts-studio__modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage prompt links</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="prompts-page__card-head">
            <div>
              <h5 className="mb-1">{selectedPrompt ? getPromptTitle(selectedPrompt) : 'Prompt'}</h5>
              <p className="text-muted mb-0">
                Link this prompt to upstream prompts so you can build reusable workflows and chains.
              </p>
            </div>
            {selectedPrompt ? (
              <span className="prompts-studio__workflow-summary">
                {selectedPromptWorkflow.upstream.length} linked
              </span>
            ) : null}
          </div>

          <div className="prompts-studio__workflow-manager">
            <div className="prompts-studio__workflow-manager-head">
              <span className="prompts-studio__workflow-label">Link another prompt</span>
              {selectedPrompt && linkingPromptId === selectedPrompt.id ? (
                <span className="prompts-studio__workflow-summary">Updating links...</span>
              ) : null}
            </div>

            <div className="prompts-studio__link-picker">
              <Form.Select
                value={dashboardLinkPicker}
                onChange={(e) => setDashboardLinkPicker(e.target.value)}
                disabled={!selectedPrompt || linkingPromptId === selectedPrompt?.id}
              >
                <option value="">Link another prompt</option>
                {availableDashboardLinks.map((prompt) => (
                  <option key={`dashboard-link-${prompt.id}`} value={prompt.id}>
                    {getPromptTitle(prompt)}
                  </option>
                ))}
              </Form.Select>
              <Button
                type="button"
                variant="outline-secondary"
                onClick={handleAddDashboardLink}
                disabled={
                  !selectedPrompt || !dashboardLinkPicker || linkingPromptId === selectedPrompt?.id
                }
              >
                Add link
              </Button>
            </div>

            <div className="prompts-studio__linked-prompt-list">
              {selectedPromptWorkflow.upstream.length > 0 ? (
                selectedPromptWorkflow.upstream.map((prompt) => (
                  <div
                    key={`modal-linked-${prompt.id}`}
                    className="prompts-studio__linked-prompt-chip"
                  >
                    <div className="prompts-studio__linked-prompt-copy">
                      <strong>{getPromptTitle(prompt)}</strong>
                      <span>{prompt.type || 'prompt'}</span>
                    </div>
                    <button
                      type="button"
                      className="prompts-studio__linked-prompt-remove"
                      onClick={() => handleRemoveDashboardLink(prompt.id)}
                      disabled={linkingPromptId === selectedPrompt?.id}
                      aria-label={`Remove ${getPromptTitle(prompt)}`}
                    >
                      Remove
                    </button>
                  </div>
                ))
              ) : (
                <div className="prompts-studio__linked-prompt-empty">
                  No direct links yet. Add one here to grow the workflow graph.
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowLinkModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        centered
        size="lg"
        dialogClassName="prompts-studio__modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Create Prompt</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="prompts-page__card-head">
            <div>
              <h5 className="mb-1">Prompt request</h5>
              <p className="text-muted mb-0">
                Set the course context, choose the workflow, and shape the AI-ready prompt you want
                to generate.
              </p>
            </div>
            <Button variant="outline-secondary" size="sm" onClick={handleResetDraft}>
              Clear draft
            </Button>
          </div>

          <Form className="prompts-page__form">
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Prompt type</Form.Label>
                  <Form.Select
                    value={isCustomPromptType ? CUSTOM_OPTION_VALUE : form.type}
                    onChange={(e) => handlePromptTypeSelectChange(e.target.value)}
                  >
                    {PROMPT_TYPE_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                    <option value={CUSTOM_OPTION_VALUE}>Custom…</option>
                  </Form.Select>
                  {isCustomPromptType ? (
                    <Form.Control
                      className="mt-2"
                      placeholder="Enter custom prompt type"
                      value={form.type}
                      onChange={(e) => handleFieldChange('type', e.target.value)}
                    />
                  ) : null}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Generation mode</Form.Label>
                  <Form.Select
                    value={isCustomGenerationMode ? CUSTOM_OPTION_VALUE : form.generationMode}
                    onChange={(e) => handleGenerationModeSelectChange(e.target.value)}
                  >
                    {GENERATION_MODE_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                    <option value={CUSTOM_OPTION_VALUE}>Custom…</option>
                  </Form.Select>
                  {isCustomGenerationMode ? (
                    <Form.Control
                      className="mt-2"
                      placeholder="Enter custom generation mode"
                      value={form.generationMode}
                      onChange={(e) => handleFieldChange('generationMode', e.target.value)}
                    />
                  ) : null}
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Course</Form.Label>
                  <Form.Select
                    value={form.courseId}
                    onChange={(e) => handleFieldChange('courseId', e.target.value)}
                  >
                    <option value="">Select a course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Project</Form.Label>
                  <Form.Select
                    value={form.projectId}
                    onChange={(e) => handleFieldChange('projectId', e.target.value)}
                  >
                    <option value="">Optional project</option>
                    {projects.map((project) => (
                      <option key={project.id} value={project.id}>
                        {project.title}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Link prompts</Form.Label>
                  <div className="prompts-studio__link-picker">
                    <Form.Select
                      value={linkedPromptPicker}
                      onChange={(e) => setLinkedPromptPicker(e.target.value)}
                    >
                      <option value="">Optional linked prompt</option>
                      {availableLinkedPrompts.map((prompt) => (
                        <option key={prompt.id} value={prompt.id}>
                          {getPromptTitle(prompt)}
                        </option>
                      ))}
                    </Form.Select>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={handleAddLinkedPrompt}
                      disabled={!linkedPromptPicker}
                    >
                      Add link
                    </Button>
                  </div>
                  <div className="prompts-studio__linked-prompt-list">
                    {selectedLinkedPrompts.length > 0 ? (
                      selectedLinkedPrompts.map((prompt) => (
                        <div
                          key={`linked-${prompt.id}`}
                          className="prompts-studio__linked-prompt-chip"
                        >
                          <div className="prompts-studio__linked-prompt-copy">
                            <strong>{getPromptTitle(prompt)}</strong>
                            <span>{prompt.type || 'prompt'}</span>
                          </div>
                          <button
                            type="button"
                            className="prompts-studio__linked-prompt-remove"
                            onClick={() => handleRemoveLinkedPrompt(prompt.id)}
                            aria-label={`Remove ${getPromptTitle(prompt)}`}
                          >
                            Remove
                          </button>
                        </div>
                      ))
                    ) : (
                      <div className="prompts-studio__linked-prompt-empty">
                        Link prompts to build chains, workflows, and prompt graphs.
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Title</Form.Label>
                  <Form.Control
                    value={form.title}
                    onChange={(e) => handleFieldChange('title', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Goal</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    value={form.goal}
                    onChange={(e) => handleFieldChange('goal', e.target.value)}
                  />
                  <div className="prompts-studio__suggestion-group">
                    {activeGoalSuggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        className="prompts-studio__suggestion-chip"
                        onClick={() => handleFieldChange('goal', suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Target outcome</Form.Label>
                  <Form.Control
                    value={form.targetOutcome}
                    onChange={(e) => handleFieldChange('targetOutcome', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Preferred stack</Form.Label>
                  <Form.Control
                    value={form.preferredStack}
                    onChange={(e) => handleFieldChange('preferredStack', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Desired output</Form.Label>
                  <Form.Control
                    list="prompt-desired-output-options"
                    value={form.desiredOutput}
                    onChange={(e) => handleFieldChange('desiredOutput', e.target.value)}
                  />
                  <datalist id="prompt-desired-output-options">
                    {DESIRED_OUTPUT_SUGGESTIONS.map((suggestion) => (
                      <option key={suggestion} value={suggestion} />
                    ))}
                  </datalist>
                  <div className="prompts-studio__hint-text">
                    Suggestions: implementation, testing, design, research, critique, teaching, or
                    automation prompts.
                  </div>
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label>Difficulty / scope</Form.Label>
                  <Form.Control
                    list="prompt-scope-options"
                    value={form.difficultyScope}
                    onChange={(e) => handleFieldChange('difficultyScope', e.target.value)}
                  />
                  <datalist id="prompt-scope-options">
                    {DIFFICULTY_SCOPE_SUGGESTIONS.map((suggestion) => (
                      <option key={suggestion} value={suggestion} />
                    ))}
                  </datalist>
                  <div className="prompts-studio__hint-text">
                    Suggestions: MVP, prototype, beginner-friendly, advanced, deep dive, or
                    production-ready.
                  </div>
                </Form.Group>
              </Col>

              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Constraints</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={form.constraints}
                    onChange={(e) => handleFieldChange('constraints', e.target.value)}
                    placeholder="One constraint per line"
                  />
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={handleGenerate} disabled={generationLoading}>
            <FaMagic className="me-2" />
            {generationLoading ? 'Generating...' : 'Generate Prompt'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};
export default PromptsPage;
